import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ApiError } from "@/lib/api";
import { useAuth } from "../hooks/useAuth";

export default function LoginPage() {
  const { login, verifyMfa, isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set once the password is accepted for a 2FA account → show the code step.
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError("Invalid email or password");
      return;
    }

    setError(null);
    setSubmitting(true);
    try {
      const challenge = await login(email.trim(), password);
      if (challenge) {
        setMfaToken(challenge.mfaToken);
        setMfaCode("");
        setUseRecoveryCode(false);
      }
    } catch (err) {
      if (err instanceof ApiError && err.status >= 500) {
        setError("Something went wrong. Please try again.");
      } else {
        setError("Invalid email or password");
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleMfaSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!mfaToken) return;
    const code = mfaCode.trim();
    if (!code) {
      setError(useRecoveryCode ? "Enter a recovery code" : "Enter the 6-digit code");
      return;
    }

    setError(null);
    setSubmitting(true);
    try {
      await verifyMfa(mfaToken, code);
    } catch (err) {
      if (err instanceof ApiError && err.status >= 500) {
        setError("Something went wrong. Please try again.");
      } else if (err instanceof ApiError && /expired/i.test(err.message)) {
        // The 5-minute step token lapsed — restart from the password.
        backToPassword();
        setError(err.message);
      } else {
        setError(err instanceof ApiError ? err.message : "Invalid verification code");
        setMfaCode("");
      }
    } finally {
      setSubmitting(false);
    }
  }

  function backToPassword() {
    setMfaToken(null);
    setMfaCode("");
    setPassword("");
    setError(null);
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 font-sans"
      style={{ backgroundColor: "hsl(210, 30%, 97%)" }}
    >
      <div className="w-full max-w-[420px]">
        <div
          className="rounded-xl bg-white p-10 shadow-sm"
          style={{ border: "1px solid hsl(210, 30%, 85%)" }}
        >
          <div className="mb-1 text-center">
            <div
              className="leading-none"
              style={{ fontWeight: 800, fontSize: "28px" }}
            >
              <span style={{ color: "hsl(210, 25%, 15%)" }}>Phaze</span>
              <span style={{ color: "hsl(210, 68%, 55%)" }}>X</span>
            </div>
            <div
              className="mt-1 text-muted-foreground"
              style={{ fontWeight: 500, fontSize: "14px" }}
            >
              Hub
            </div>
          </div>

          <p
            className="mt-4 text-center text-muted-foreground"
            style={{ fontSize: "14px", marginBottom: "2rem" }}
          >
            {mfaToken ? "Two-factor verification" : "Sign in to your workspace"}
          </p>

          {mfaToken ? (
            <form onSubmit={handleMfaSubmit} className="space-y-4" noValidate>
              <div className="space-y-1.5">
                <Label htmlFor="mfa-code">
                  {useRecoveryCode ? "Recovery code" : "Authentication code"}
                </Label>
                <Input
                  id="mfa-code"
                  key={useRecoveryCode ? "recovery" : "totp"}
                  type="text"
                  inputMode={useRecoveryCode ? "text" : "numeric"}
                  autoComplete="one-time-code"
                  autoFocus
                  maxLength={useRecoveryCode ? 9 : 6}
                  placeholder={useRecoveryCode ? "XXXX-XXXX" : "123456"}
                  value={mfaCode}
                  onChange={(e) =>
                    setMfaCode(
                      useRecoveryCode
                        ? e.target.value.toUpperCase()
                        : e.target.value.replace(/\D/g, ""),
                    )
                  }
                  disabled={submitting}
                  className="h-9 text-center tracking-[0.3em]"
                />
                <p className="text-muted-foreground" style={{ fontSize: "12px" }}>
                  {useRecoveryCode
                    ? "Each recovery code can be used once."
                    : "Open your authenticator app and enter the current code for PhazeX Hub."}
                </p>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-9"
                style={{
                  backgroundColor: "hsl(210, 68%, 55%)",
                  fontWeight: 500,
                }}
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Verifying…
                  </>
                ) : (
                  "Verify"
                )}
              </Button>

              <div
                className="flex items-center justify-between"
                style={{ fontSize: "13px" }}
              >
                <button
                  type="button"
                  onClick={backToPassword}
                  className="text-muted-foreground hover:text-foreground"
                  disabled={submitting}
                >
                  ← Back
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setUseRecoveryCode((v) => !v);
                    setMfaCode("");
                    setError(null);
                  }}
                  className="hover:underline"
                  style={{ color: "hsl(210, 68%, 45%)" }}
                  disabled={submitting}
                >
                  {useRecoveryCode ? "Use authenticator app" : "Use a recovery code"}
                </button>
              </div>

              {error && (
                <p
                  className="text-center"
                  style={{
                    color: "hsl(0, 72%, 51%)",
                    fontSize: "13px",
                    marginTop: "0.75rem",
                  }}
                  role="alert"
                >
                  {error}
                </p>
              )}

              {useRecoveryCode && (
                <p
                  className="text-center text-muted-foreground"
                  style={{ fontSize: "12px" }}
                >
                  Lost your device and recovery codes? Ask an administrator to
                  reset your two-factor authentication.
                </p>
              )}
            </form>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email address</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
                className="h-9"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting}
                  className="h-9 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground focus:outline-none"
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-9"
              style={{
                backgroundColor: "hsl(210, 68%, 55%)",
                fontWeight: 500,
              }}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Signing in…
                </>
              ) : (
                "Sign in"
              )}
            </Button>

            {error && (
              <p
                className="text-center"
                style={{
                  color: "hsl(0, 72%, 51%)",
                  fontSize: "13px",
                  marginTop: "0.75rem",
                }}
                role="alert"
              >
                {error}
              </p>
            )}
          </form>
          )}
        </div>

        <p
          className="mt-6 text-center text-muted-foreground"
          style={{ fontSize: "12px" }}
        >
          PhazeX Hub · Enterprise Platform
        </p>
      </div>
    </div>
  );
}
